/**
 * Projects API Hook
 */

import { useApiData, useAuthenticatedFetch } from './client';
import type { Project } from './types';

export function useProjects(options?: {
  underMatterId?: number;
  underClientId?: number;
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
  /** Cache-buster: change to force a refetch (backend ignores it). */
  refreshKey?: number;
}) {
  // NOTE: api-edna accepts `limit`, `order_by`, and `ascending` only. It has no
  // `skip`/`offset`/cursor, and no eager-loading flags — collections are capped at
  // 1000 with truncation signalled in the `X-Result-Truncated` response header.
  // Options it ignores were removed rather than left as silent no-ops.
  const params = new URLSearchParams();
  if (options?.underMatterId !== undefined) params.set('under_matter_id', String(options.underMatterId));
  if (options?.underClientId !== undefined) params.set('under_client_id', String(options.underClientId));
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.orderBy) params.set('order_by', options.orderBy);
  if (options?.ascending !== undefined) params.set('ascending', String(options.ascending));
  if (options?.refreshKey) params.set('_r', String(options.refreshKey));

  const queryString = params.toString();
  const endpoint = queryString ? `/projects?${queryString}` : '/projects';

  return useApiData<Project[]>(endpoint);
}

export function useProject(projectId: number) {
  return useApiData<Project>(`/projects/${projectId}`);
}

export function useProjectMutations() {
  const { fetchData } = useAuthenticatedFetch<Project>();

  const createProject = (data: {
    name: string;
    id_matter: number;
    version: number;
    projecttype: 'static' | 'continuous';
    start_ts?: string;
    end_ts?: string;
  }) =>
    fetchData('/projects', {
      method: 'POST',
      body: JSON.stringify({ ...data, version: String(data.version) }),
    });

  const updateProject = (id: number, data: Partial<Project>) => {
    const payload = { ...data, version: data.version != null ? String(data.version) : undefined };
    return fetchData(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  };

  const deleteProject = (id: number) =>
    fetchData(`/projects/${id}`, { method: 'DELETE' });

  return { createProject, updateProject, deleteProject };
}
